import React from 'react';
import type { Session } from 'state';

import { formatters } from 'services';
import PriceCell from 'reusable/PriceCell';

import type { ExecutionIntent } from './executionModel';
import { executionTargetsInstrument } from './executionModel';
import InstrumentActions from './InstrumentActions';
import { ordinaryQuoteIsAvailable } from './model';
import type { MarketInstrument, SelectInstrument } from './types';

type Props = {
  rows: MarketInstrument[];
  session: Session;
  reportingCurrencySymbol: string;
  selectedInstrumentId?: number;
  selectInstrument: SelectInstrument;
  showOrderModal: (instrumentId: number) => void;
  showODAModal: (instrumentId: number) => void;
  executionIntent: ExecutionIntent;
};

const pnlClass = (value: number) =>
  value > 0 ? 'is-positive' : value < 0 ? 'is-negative' : '';

const BondsMarket: React.FC<Props> = ({
  rows,
  session,
  reportingCurrencySymbol,
  selectedInstrumentId,
  selectInstrument,
  showOrderModal,
  showODAModal,
  executionIntent,
}) => (
  <div className="markets-v2-bond-scroll markets-v3-bond-scroll">
    <table className="markets-v2-bond-table markets-v3-bond-table">
      <thead>
        <tr>
          <th>Instrument</th>
          <th>Availability</th>
          <th className="is-number">Terms</th>
          <th className="is-number">Market</th>
          <th className="is-number">Position &amp; risk</th>
          <th className="is-number">P&amp;L · {reportingCurrencySymbol}</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(row => {
          const quoted = ordinaryQuoteIsAvailable(row.asset);
          const scheduled =
            !quoted &&
            Boolean(row.asset.asset_issue_date) &&
            new Date(row.asset.asset_issue_date).getTime() >
              new Date(session.scenario_date).getTime();
          const selected = selectedInstrumentId === row.instrument.id;
          const inDesk = executionTargetsInstrument(
            executionIntent,
            row.instrument.id,
          );
          const focus = () => selectInstrument(row.instrument);

          return (
            <tr
              key={row.instrument.id}
              className={`${selected ? 'is-selected' : ''}${
                inDesk ? ' is-in-desk' : ''
              }${quoted ? '' : ' is-upcoming'}`}
              onClick={focus}
            >
              <td>
                <div className="markets-v2-bond-name">
                  <strong title={row.asset.name}>
                    {row.asset.domicile || row.asset.geography || 'Global'} Govt
                    · {formatters.commasExact3Digits(row.asset.coupon)}% ·{' '}
                    {formatters.commas2Digits(
                      row.instrument.htm_afs_years_to_maturity,
                    )}
                    Y
                  </strong>
                  <span title={row.asset.ticker}>{row.asset.ticker}</span>
                  <small>
                    {row.asset.issuer_type || 'Govt.'} ·{' '}
                    {row.asset.domicile || row.asset.geography}
                  </small>
                </div>
              </td>
              <td>
                <span
                  className={`markets-v2-quote-state ${
                    quoted ? 'is-live' : 'is-waiting'
                  }`}
                >
                  <i />
                  {quoted
                    ? 'Live now'
                    : scheduled
                      ? 'Future vintage'
                      : 'Quote unavailable'}
                </span>
                {!quoted && (
                  <span className="markets-v2-cell-note">
                    {scheduled
                      ? 'Visible now · activates when issued'
                      : 'Visible now · awaiting usable quote'}
                  </span>
                )}
              </td>
              <td className="is-number">
                <strong>
                  {formatters.commas2Digits(
                    row.instrument.htm_afs_years_to_maturity,
                  )}{' '}
                  yrs
                </strong>
                <span>
                  {formatters.commasExact3Digits(
                    row.instrument.htm_afs_duration,
                  )}{' '}
                  dur · {formatters.commasExact3Digits(row.asset.coupon)}%
                </span>
                <span>
                  {row.instrument.htm_afs_days_to_coupon || '—'}d to coupon
                </span>
              </td>
              <td className="is-number">
                <strong>
                  {quoted
                    ? `${formatters.commasExact3Digits(row.asset.ask_yield)}% yield`
                    : '— yield'}
                </strong>
                {quoted ? (
                  <div className="markets-v2-bond-quote">
                    <span>
                      Bid{' '}
                      <strong>
                        <PriceCell
                          price={formatters.priceFormat(
                            row.asset.bid,
                            row.asset.ticker,
                            row.currencySymbol,
                            row.asset.resourcetype,
                          )}
                          priceDiff={row.asset.bid_diff}
                        />
                      </strong>
                    </span>
                    <span>
                      Ask{' '}
                      <strong>
                        <PriceCell
                          price={formatters.priceFormat(
                            row.asset.ask,
                            row.asset.ticker,
                            row.currencySymbol,
                            row.asset.resourcetype,
                          )}
                          priceDiff={row.asset.ask_diff}
                        />
                      </strong>
                    </span>
                  </div>
                ) : (
                  <span className="markets-v2-muted-dash">— / —</span>
                )}
              </td>
              <td className="is-number">
                <strong>
                  {formatters.commasNoDigits(row.instrument.amount)} ·{' '}
                  {formatters.commasNoDigits(row.reportingValue)}{' '}
                  {reportingCurrencySymbol}
                </strong>
                <span>
                  DV01 {formatters.commas2Digits(row.instrument.htm_afs_dv01)}
                </span>
              </td>
              <td className="is-number">
                <strong
                  className={pnlClass(row.instrument.reporting_unrealized_pnl)}
                >
                  {formatters.commasNoDigits(
                    row.instrument.reporting_unrealized_pnl,
                  )}
                </strong>
                <span>
                  {formatters.commasNoDigits(
                    row.instrument.reporting_realized_pnl,
                  )}{' '}
                  realized
                </span>
              </td>
              <td>
                <InstrumentActions
                  row={row}
                  session={session}
                  selected={selected}
                  onFocus={focus}
                  onTrade={() => {
                    focus();
                    showOrderModal(row.instrument.id);
                  }}
                  onODA={() => {
                    focus();
                    showODAModal(row.instrument.id);
                  }}
                  tradeOpen={inDesk && executionIntent.kind === 'order'}
                  odaOpen={inDesk && executionIntent.kind === 'oda'}
                />
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

export default BondsMarket;
